/**
 * The pictures the graph window draws (the nodes' attached images, in the nodal view and in
 * the class diagram's boxes): each read once from the project's assets, as an `Image`, and the
 * view repainted when it has loaded.
 */
import type { AssetsService } from "../../state/assets";

/** Pictures by asset path: a loaded one, or `null` while it loads (or is not there). */
export type PictureCache = (path: string) => HTMLImageElement | null;

/** A cache reading pictures from `assets`; `loaded` is called when one arrives (to repaint). */
export function createPictureCache(assets: Pick<AssetsService, "url">, loaded: () => void): PictureCache {
  /** The images made, by address (an asset changed gets a new address, so a new image). */
  const images = new Map<string, HTMLImageElement>();
  return (path) => {
    const url = assets.url(path);
    if (!url) return null;
    let image = images.get(url);
    if (!image) {
      image = new Image();
      image.onload = loaded;
      image.src = url;
      images.set(url, image);
    }
    return image.complete && image.naturalWidth ? image : null;
  };
}
